import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { post } from "@/lib/api";
import { useToast } from "@/components/ToastProvider";

interface MarketingActionsMenuProps {
  context?: Record<string, unknown>;
}

interface ActionItem {
  id: string;
  label: string;
  description: string;
  requiresApproval?: boolean;
}

const ACTIONS: ActionItem[] = [
  {
    id: "marketing.generate_tour_announcement",
    label: "Generate Tour Announcement Pack",
    description: "Assets + copy for the next tour stop.",
    requiresApproval: true,
  },
  {
    id: "marketing.generate_regional_press",
    label: "Generate Regional Press Push",
    description: "Draft outreach + pitch bundles per city.",
    requiresApproval: true,
  },
  {
    id: "marketing.populate_week_of_content",
    label: "Auto-Populate Week-of Content",
    description: "Fill calendar slots based on KPIs and nudges.",
  },
  {
    id: "marketing.create_ticket_countdown",
    label: "Create Ticket Countdown",
    description: "Scheduled countdown posts with CTA links.",
  },
  {
    id: "mixboard.trigger_variant",
    label: "Trigger Mixboard Variant Session",
    description: "Send session context to Mixboard for new variants.",
  },
  {
    id: "auto_editor.request_clip_batch",
    label: "Request Auto-Editor Clip Batch",
    description: "Auto-Editor will cut highlight clips from footage.",
    requiresApproval: false,
  },
];

export function MarketingActionsMenu({ context }: MarketingActionsMenuProps) {
  const { push } = useToast();
  const [pending, setPending] = useState<string | null>(null);

  const handleTrigger = async (action: ActionItem) => {
    if (pending) return;
    setPending(action.id);
    try {
      await post("/orchestration/trigger", {
        action: action.id,
        context: context ?? {},
      });
      push({
        title: action.requiresApproval ? "Submitted for review" : "Action queued",
        message: action.requiresApproval
          ? "It will appear in the HITL inbox for approval."
          : "It will run in the orchestration queue shortly.",
      });
    } catch (error) {
      push({
        title: "Unable to trigger action",
        message: error instanceof Error ? error.message : "Try again shortly.",
        variant: "destructive",
      });
    } finally {
      setPending(null);
    }
  };

  return (
    <Card className="space-y-3 border-border/70 bg-background/40 p-4">
      <div>
        <p className="text-sm font-semibold text-foreground">Automations</p>
        <p className="text-xs text-muted-foreground">Invoke orchestration flows across agents.</p>
      </div>
      <div className="space-y-2">
        {ACTIONS.map((action) => (
          <div key={action.id} className="rounded-lg border border-border/60 bg-background/40 p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">{action.label}</p>
              {action.requiresApproval ? (
                <Badge variant="secondary" className="border-warning/50 bg-warning/10 text-warning">
                  HITL
                </Badge>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground">{action.description}</p>
            <Button
              size="sm"
              className="mt-3 w-full"
              disabled={Boolean(pending) && pending !== action.id}
              onClick={() => handleTrigger(action)}
            >
              {pending === action.id ? "Triggering…" : "Trigger"}
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
